import type { Editor } from '@tiptap/react';

/**
 * Drag-and-drop helpers for moving a photo between (or within) image grids.
 *
 * A filled cell is dragged with a GRID_CELL_MIME payload carrying its source
 * grid position, cell index, and image data. On drop, the photo is relocated
 * into the target cell — a plain move when the target is empty, or a swap when
 * it's filled (so no photo is lost). Because both edits are attribute-only,
 * source and target positions stay valid within a single transaction even when
 * they belong to two different grids — no position remapping needed.
 */

export const GRID_CELL_MIME = 'application/x-odo-grid-cell';

export interface CellDragPayload {
    gridPos: number;
    cellIndex: number;
    src: string;
    fileId: string;
    caption: string;
}

export function serializeCellDragPayload(payload: CellDragPayload): string {
    return JSON.stringify(payload);
}

export function parseCellDragPayload(data: string): CellDragPayload | null {
    try {
        const parsed = JSON.parse(data) as Partial<CellDragPayload>;
        if (
            typeof parsed.gridPos === 'number' &&
            typeof parsed.cellIndex === 'number' &&
            typeof parsed.src === 'string' &&
            typeof parsed.fileId === 'string'
        ) {
            return {
                ...(parsed as CellDragPayload),
                caption: typeof parsed.caption === 'string' ? parsed.caption : ''
            };
        }
    } catch {
        // malformed payload — ignored
    }
    return null;
}

interface CellData {
    src: string | null;
    fileId: string | null;
    caption: string;
}

interface MoveTarget {
    gridPos: number;
    cellIndex: number;
    cell: CellData;
}

/**
 * Relocate the dragged photo into the target cell (move or swap) in one
 * transaction. No-ops if dropped onto its own cell or if either position is
 * stale (the doc changed since dragstart).
 */
export function moveGridCell(editor: Editor, payload: CellDragPayload, target: MoveTarget): void {
    if (payload.gridPos === target.gridPos && payload.cellIndex === target.cellIndex) return;

    const cellType = editor.schema.nodes.imageGridCell;
    const doc = editor.state.doc;
    const targetCellPos = target.gridPos + 1 + target.cellIndex;
    const sourceCellPos = payload.gridPos + 1 + payload.cellIndex;
    if (doc.nodeAt(targetCellPos)?.type !== cellType) return;
    if (doc.nodeAt(sourceCellPos)?.type !== cellType) return;

    const tr = editor.state.tr;
    tr.setNodeMarkup(targetCellPos, cellType, {
        src: payload.src,
        fileId: payload.fileId,
        caption: payload.caption
    });
    tr.setNodeMarkup(sourceCellPos, cellType, {
        src: target.cell.src,
        fileId: target.cell.fileId,
        caption: target.cell.caption
    });
    editor.view.dispatch(tr);
}
